import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle, ArrowRight, XCircle } from "@phosphor-icons/react";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [amount, setAmount] = useState(null);
  const [tries, setTries] = useState(0);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setAmount(data.amount);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (data.payment_status === "expired" || data.status === "failed") { setStatus("failed"); return; }
        if (tries < 12) { setTries((t) => t + 1); setTimeout(poll, 1500); }
        else setStatus("pending");
      } catch { if (!cancelled) setStatus("failed"); }
    };
    poll();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="max-w-xl mx-auto px-6 py-24 text-center">
      {status === "paid" ? (
        <>
          <div className="w-14 h-14 mx-auto border border-neutral-900 rounded-full flex items-center justify-center mb-6">
            <CheckCircle size={28} weight="regular"/>
          </div>
          <div className="label-eyebrow mb-3">Payment confirmed</div>
          <h1 data-testid="payment-success-title" className="font-display text-3xl font-medium tracking-tight mb-4">Your den is booked.</h1>
          {amount ? <p className="text-neutral-600 mb-8">${amount.toFixed(2)} charged. A receipt is on the way.</p> : null}
          <Link to="/trips"><Button data-testid="go-to-trips-btn" className="h-11 bg-neutral-900 hover:bg-neutral-800">View my trips <ArrowRight size={14} className="ml-1"/></Button></Link>
        </>
      ) : status === "polling" ? (
        <div className="text-neutral-500">Confirming your payment...</div>
      ) : status === "pending" ? (
        <>
          <h1 className="font-display text-2xl font-medium mb-2">Payment pending</h1>
          <p className="text-neutral-600 mb-6">This can take a moment. Check "My trips" shortly.</p>
          <Link to="/trips"><Button variant="outline" className="border-neutral-300">Go to trips</Button></Link>
        </>
      ) : (
        <>
          <div className="w-14 h-14 mx-auto border border-red-500 rounded-full flex items-center justify-center mb-6 text-red-500">
            <XCircle size={28}/>
          </div>
          <h1 className="font-display text-2xl font-medium mb-2">Something went wrong</h1>
          <Link to="/trips"><Button variant="outline" className="border-neutral-300">Back to trips</Button></Link>
        </>
      )}
    </div>
  );
}

export function PaymentCancel() {
  const [params] = useSearchParams();
  const bookingId = params.get("booking_id");
  return (
    <div className="max-w-xl mx-auto px-6 py-24 text-center">
      <div className="label-eyebrow mb-3">Payment cancelled</div>
      <h1 className="font-display text-3xl font-medium tracking-tight mb-4">Your booking is still on hold.</h1>
      <p className="text-neutral-600 mb-8">You can pay to secure it any time from your trips page — the host is holding the slot for you.</p>
      <div className="flex justify-center gap-3">
        <Link to="/trips"><Button data-testid="cancel-back-btn" className="h-11 bg-neutral-900 hover:bg-neutral-800">Back to trips</Button></Link>
        <Link to="/browse"><Button variant="outline" className="border-neutral-300 h-11">Keep browsing</Button></Link>
      </div>
    </div>
  );
}
